/**
 * React Query hooks for ATS onboarding checklist.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useLocale } from '@aaas/shared';
import {
  getDefaultEmployeeTypes,
  getDefaultPipelines,
  getDefaultRecruitmentTypes,
  getDefaultRejectionReasons,
  getDefaultSources,
  getDefaultWorkArrangements,
} from '../constants/referenceDefaults';
import employeeTypeService from '../services/employee-type.service';
import onboardingService from '../services/onboarding.service';
import pipelineService from '../services/pipeline.service';
import recruitmentTypeService from '../services/recruitment-type.service';
import rejectionReasonService from '../services/rejection-reason.service';
import sourceService from '../services/source.service';
import workArrangementService from '../services/work-arrangement.service';
import type {
  OnboardingProgress,
  OnboardingStepKey,
  SeedSampleDataAggregate,
} from '../types/onboarding';

const queryKey = (workspaceId: string | undefined) => [
  'ats',
  'onboarding',
  workspaceId,
];

export function useOnboardingProgress() {
  const { currentWorkspace, idToken } = useAuth();

  return useQuery<OnboardingProgress>({
    queryKey: queryKey(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id || !idToken) {
        throw new Error('Workspace and auth required');
      }
      return onboardingService.getProgress(currentWorkspace.id, idToken);
    },
    enabled: !!currentWorkspace?.id && !!idToken,
    // Counts (positions, campaigns, jobs, CVs, members) may have changed
    // while the user was on another page. Always refetch on mount so the
    // checklist reflects the latest state when they return to /ats/dashboard.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useDismissDashboard() {
  const { currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id || !idToken) throw new Error('Not ready');
      return onboardingService.dismissDashboard(currentWorkspace.id, idToken);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey(currentWorkspace?.id), data);
    },
  });
}

export function useDismissStep() {
  const { currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (step: OnboardingStepKey) => {
      if (!currentWorkspace?.id || !idToken) throw new Error('Not ready');
      return onboardingService.dismissStep(currentWorkspace.id, step, idToken);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey(currentWorkspace?.id), data);
    },
  });
}

/**
 * "Tạo dữ liệu mẫu" orchestrator.
 *
 * Sequence:
 *   1. Bulk-create reference data (Source, EmployeeType, WorkArrangement,
 *      RecruitmentType, RejectionReason, Pipeline+rounds) — 6 backend calls,
 *      duplicates skipped silently per endpoint contract.
 *   2. Once reference data exists, POST /onboarding/sample-data to seed
 *      Position / Campaign / Job / CV using the just-created reference rows.
 *
 * The hook surfaces a single aggregate result so the UI can show one
 * success message.
 */
export function useSeedSampleData() {
  const { currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();
  const { locale } = useLocale();

  return useMutation<SeedSampleDataAggregate, Error>({
    mutationFn: async () => {
      if (!currentWorkspace?.id || !idToken) throw new Error('Not ready');
      const wsId = currentWorkspace.id;

      const [
        sourcesResult,
        employeeTypesResult,
        workArrangementsResult,
        recruitmentTypesResult,
        rejectionReasonsResult,
        pipelinesResult,
      ] = await Promise.all([
        sourceService.bulkCreateSources(wsId, idToken, getDefaultSources(locale)),
        employeeTypeService.bulkCreateEmployeeTypes(
          wsId,
          idToken,
          getDefaultEmployeeTypes(locale),
        ),
        workArrangementService.bulkCreateWorkArrangements(
          wsId,
          idToken,
          getDefaultWorkArrangements(locale),
        ),
        recruitmentTypeService.bulkCreateRecruitmentTypes(
          wsId,
          idToken,
          getDefaultRecruitmentTypes(locale),
        ),
        rejectionReasonService.bulkCreateRejectionReasons(
          wsId,
          idToken,
          getDefaultRejectionReasons(locale),
        ),
        pipelineService.bulkCreatePipelines(wsId, idToken, getDefaultPipelines(locale)),
      ]);

      // Reference data is now in place — seed the business records.
      const business = await onboardingService.seedBusinessData(wsId, idToken);

      return {
        sourcesCreated: sourcesResult.created,
        employeeTypesCreated: employeeTypesResult.created,
        workArrangementsCreated: workArrangementsResult.created,
        recruitmentTypesCreated: recruitmentTypesResult.created,
        rejectionReasonsCreated: rejectionReasonsResult.created,
        pipelinesCreated: pipelinesResult.created,
        positions: business.positions,
        campaigns: business.campaigns,
        jobs: business.jobs,
        cvs: business.cvs,
      };
    },
    onSuccess: () => {
      // Refresh progress + invalidate downstream lists conservatively.
      queryClient.invalidateQueries({ queryKey: queryKey(currentWorkspace?.id) });
      queryClient.invalidateQueries({ queryKey: ['sources', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['employee-types', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['work-arrangements', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['recruitment-types', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['rejection-reasons', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['positions', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['cvs', currentWorkspace?.id] });
    },
  });
}
